import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Footer } from './components';
import BannerImage from '../assets/banner.png';
import GlobalHeader from '../components/GlobalHeader';
import { LPSectionHero } from './legacy/LegacyHero';
import LPSectionInfluencers from './legacy/LegacyInfluencers';
import { LPSectionBrands } from './legacy/LegacyBrands';
import LPSectionProducts from './legacy/LegacyProducts';
import LPSectionBlogs from './legacy/LegacyBlogs';
import PaddedContainer from '../components/PaddedContainer ';
//import StoryAndBrand from './components/StoryAndBrand';
import { Box, useMediaQuery, useTheme } from '@mui/system';
import { Typography } from '@mui/material';
import StartCreatingButton from '../components/StartCreatingButton ';
import Background from '../assets/Background.png';
import useAuthToken from '../hooks/useAuthToken';
import { useEffect, useState } from 'react';
import { StoreError } from '../hooks/useNavigation';
import userClient from '../utils/apiClients/userClient';
import { handleSignOut } from '../handlers/signOut';
import LoadingIcon from '../icons/LoadingIcon';
import { signIn } from 'aws-amplify/auth';

// ─── Brands FAQ ───────────────────────────────────────────────────────────────

const brandsFaqs = [
  {
    q: 'How does Saagas source creators?',
    a: 'We work with a retained roster of vetted UGC creators across consumer, lifestyle, and B2B/workplace verticals. Every creator is contracted, payment-onboarded, and pre-screened for content fit — no gig-marketplace browsing on your end.',
  },
  {
    q: 'Is AI required? What if we only want traditional UGC?',
    a: 'AI is optional. We support both traditional UGC content production and AI-generated content from creator Personas. Most campaigns mix both based on what the brief needs — you tell us, we run it.',
  },
  {
    q: 'How does payment to creators work?',
    a: 'Saagas handles all creator payments end-to-end. You pay us a single invoice per campaign; we handle creator escrow, payouts, and 1099s via Stripe.',
  },
  {
    q: 'How is content licensed?',
    a: 'Each piece of content comes with a clear, written license: platforms, channels, paid/organic rights, duration, and exclusivity. You see the license terms before you book the campaign — no surprises mid-flight.',
  },
  {
    q: "What happens if a creator's content underperforms?",
    a: "We cut underperformers from the rotation by week 3 of any campaign and reallocate budget to winners. You don't pay for fixed-roster slots — performance reporting drives the next week's variants.",
  },
];

const BrandsInlineFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <Box
      component="section"
      sx={{
        pt: { xs: '2.5rem', md: '4rem' },
        pb: { xs: '2rem', md: '3rem' },
        px: { xs: '1rem', md: '2rem' },
        maxWidth: '800px',
        mx: 'auto',
        width: '100%',
      }}
    >
      <Typography
        fontFamily="Inter"
        component="h2"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '24px', md: '34px' },
          color: '#FFFFFF',
          textAlign: 'center',
          mb: 4,
        }}
      >
        Common questions.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {brandsFaqs.map(({ q, a }, i) => (
          <Box key={q} sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', overflow: 'hidden' }}>
            <Box
              component="button"
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left',
                px: 2.5,
                py: 2,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#FFFFFF',
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: { xs: '14px', md: '15px' },
                '&:hover': { background: 'rgba(255,255,255,0.04)' },
              }}
            >
              <span>{q}</span>
              <Box
                component="span"
                sx={{
                  ml: 2,
                  flexShrink: 0,
                  transition: 'transform 0.2s',
                  transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: '#808080',
                  fontSize: '18px',
                }}
              >
                ▾
              </Box>
            </Box>
            {openIndex === i && (
              <Box sx={{ px: 2.5, pb: 2.5, background: 'rgba(255,255,255,0.03)' }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '14px', color: '#C0C0C0', lineHeight: '160%' }}>
                  {a}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Brands — Payments management section ─────────────────────────────────────

const PaymentsBlock = () => (
  <Box
    component="section"
    sx={{
      pt: { xs: '3rem', md: '5rem' },
      pb: { xs: '2rem', md: '3rem' },
      px: { xs: '1rem', md: '2rem' },
      maxWidth: '780px',
      mx: 'auto',
      width: '100%',
      textAlign: 'center',
    }}
  >
    <Typography
      fontFamily="Inter"
      component="h2"
      sx={{
        fontWeight: 700,
        fontSize: { xs: '24px', md: '34px' },
        color: '#FFFFFF',
        mb: 2,
      }}
    >
      Payments handled. End to end.
    </Typography>
    <Typography
      fontFamily="Inter"
      sx={{
        fontWeight: 400,
        fontSize: { xs: '15px', md: '17px' },
        color: '#C0C0C0',
        lineHeight: '160%',
        mb: 1.5,
      }}
    >
      You pay Saagas once per campaign. We pay every creator directly when their work is delivered and approved.
    </Typography>
    <Typography
      fontFamily="Inter"
      sx={{
        fontWeight: 300,
        fontSize: { xs: '14px', md: '16px' },
        color: '#A0A0A0',
        lineHeight: '160%',
      }}
    >
      No per-creator contracts. No chasing invoices. No 1099s, W-9s, or international payouts on your end — every payment, currency conversion, and tax form flows through us.
    </Typography>
  </Box>
);

const OriginalLandingPage = () => {
  const theme = useTheme();
  const isMdOrBelow = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState<boolean>(false);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const { isAuthenticated, isLoading: tokenLoading } = useAuthToken();
  const location = useLocation();

  useEffect(() => {
    const fetchUser = async () => {
      if (loading) return;
      try {
        setLoading(true);
        if (!isAuthenticated) return;
        const { data }: { data: { user: object } } = await userClient.post('/login', {});
        if (!data.user) {
          setIsWhitelisted(true);
          return;
        }
      } catch (error) {
        StoreError('An error is generated while calling api /login ', error);
        await handleSignOut();
        throw error;
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [isAuthenticated, loading]);

  useEffect(() => {
    if (location.hash) {
      const targetElement = document.querySelector(location.hash);
      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]);

  useEffect(() => {
    const logs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
    if (logs.length > 0) {
      logs.forEach(log => {
        console.error(log);
      });
      localStorage.removeItem('errorLogs');
    }
  }, []);

  if (loading || tokenLoading || window.location.href.includes('app')) {
    return (
      <div className="grid place-items-center h-screen w-full">
        <div>
          <LoadingIcon />
          <p className="font-inter text-semibold mt-4">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (isWhitelisted) {
    return (
      <div className="grid place-items-center h-screen w-full">
        <div className="flex flex-col items-center">
          <LoadingIcon width={180} />
          <p className="font-inter text-semibold mt-4">Thank you for your interest!</p>
          <p className="font-inter text-semibold mt-4">You are on the waitlist. Please wait, while we get you onboard!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden bg-green cursor-default">
      <>
        {/* <LandingHeader /> */}
        <GlobalHeader />
        {/* Hero section */}
        <PaddedContainer className="">
          <LPSectionHero />
        </PaddedContainer>
        <PaddedContainer bgColor="#0C0211">
          <LPSectionInfluencers />
        </PaddedContainer>
        <Box id="brands">
          <PaddedContainer bgColor="#151515">
            <LPSectionBrands />
          </PaddedContainer>
        </Box>
        <PaddedContainer bgColor="#0000">
          <LPSectionProducts />
        </PaddedContainer>                
        <PaddedContainer bgColor="linear-gradient(195.36deg, rgba(16, 166, 202, 0.2) -110.46%, rgba(86, 20, 110, 0.2) 100%)">
          <Box
            className="max-w-[1256px] m-auto relative px-6 md:pb-20"
            sx={{
              display: {
                xs: 'none',
                md: 'block'
              }
            }}
          >
            <LPSectionBlogs />
          </Box>
        </PaddedContainer>
        <PaddedContainer>
          <Box
            sx={{
              overflow: 'hidden',
              width: '100%',
              height: {
                xs: '507px',
                md: '507px',
                lg: '422px'
              },
              backgroundImage: `url(${BannerImage})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}
          >
            <Box sx={{ textAlign: 'center', mt: '3.75rem' }}>
              <Typography
                fontFamily="Inter"
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '40px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  color: '#FFFFFF'
                }}
              >
                Join UGC Creators and Brands
              </Typography>

              <Typography
                fontFamily="Inter"
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '24px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  color: '#FFFFFFCC',
                  mt: 2
                }}
              >
                who trust us to manage their UGC licensing needs
              </Typography>

              <Button
                as="a"
                href="https://t.co/401XPwdXQx"
                target="_blank"
                rel="noreferrer"
                variant="primary"
                style={{
                  border: '1px solid #FAFAFA',
                  backgroundColor: 'transparent',
                  width: '220px',
                  maxHeight: '60px',
                  borderRadius: '30px',
                  padding: '15px 16px',
                  color: '#FAFAFA',
                  fontWeight: 700,
                  fontSize: '20px',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textDecoration: 'none',
                  margin: '2rem auto 0'
                }}
              >
                Join our discord
              </Button>
            </Box>
          </Box>
        </PaddedContainer>
        {isMdOrBelow ? (
          <PaddedContainer>
            <Box
              sx={{
                backgroundImage: `url(${Background})`,
                backgroundSize: 'cover',
                width: '100%',
                height: '442px',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                my: '4rem'
              }}
            >
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '32px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  color: '#FFFFFF',
                  px: 2
                }}
              >
                Start creating now
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '24px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  color: '#FFFFFFCC',
                  px: 2,
                  mt: 2
                }}
              >
                Upholding the value of human artistry in the age of AI.
              </Typography>
              <StartCreatingButton />
            </Box>
          </PaddedContainer>
        ) : (
          ''
        )}
        {/* Payments management */}
        <PaddedContainer>
          <PaymentsBlock />
        </PaddedContainer>

        {/* FAQ */}
        <PaddedContainer>
          <BrandsInlineFAQ />

          {/* Pricing callout (mirrors the FAQs block above) */}
          <Box
            component="section"
            sx={{
              pt: { md: '6rem' },
              pb: { md: '2rem' },
              px: { xs: '1rem', md: '0' },
              display: { lg: 'block', md: 'none', xs: 'none' }
            }}
          >
            <Typography
              fontFamily="Inter"
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '1.875rem' },
                lineHeight: '100%',
                letterSpacing: '0px',
                textAlign: 'center',
                verticalAlign: 'middle',
                color: '#FFFFFFCC',
                opacity: 1
              }}
            >
              See pricing for FairGen AI Studio
            </Typography>
            <a
              href="https://saagas.ai/pricing"
              target="_blank"
              rel="noreferrer"
              style={{
                border: '1px solid #FAFAFA',
                backgroundColor: 'transparent',
                maxWidth: '160px',
                maxHeight: '60px',
                borderRadius: '30px',
                padding: '20px 24px',
                color: '#FAFAFA',
                fontWeight: 500,
                fontSize: '20px',
                fontFamily: 'Inter',
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textDecoration: 'none',
                margin: '2rem auto 0',
                width: 'fit-content'
              }}
            >
              Pricing
            </a>
          </Box>

          {/* Mobile variant of the Pricing callout */}
          <Box
            component="section"
            sx={{
              pt: { xs: '3rem' },
              pb: { xs: '2rem' },
              px: { xs: '1rem' },
              display: { lg: 'none', xs: 'block' }
            }}
          >
            <Typography
              fontFamily="Inter"
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '1.25rem',
                lineHeight: '122%',
                textAlign: 'center',
                color: '#FFFFFFCC'
              }}
            >
              See pricing for <br/> FairGen AI Studio
            </Typography>
            <a
              href="https://saagas.ai/pricing"
              target="_blank"
              rel="noreferrer"
              style={{
                border: '1px solid #FAFAFA',
                backgroundColor: 'transparent',
                maxWidth: '140px',
                maxHeight: '54px',
                borderRadius: '30px',
                padding: '14px 22px',
                color: '#FAFAFA',
                fontWeight: 500,
                fontSize: '16px',
                fontFamily: 'Inter',
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textDecoration: 'none',
                margin: '1.5rem auto 0',
                width: 'fit-content'
              }}
            >
              Pricing
            </a>
          </Box>
        </PaddedContainer>
        {isMdOrBelow ? (
          ''
        ) : (
          <PaddedContainer>
            <Box sx={{ textAlign: 'center', mt: 14, pb: { xs: '2rem', md: '0', lg: '4rem' } }}>
              <Typography
                fontFamily="Inter"
                component="h3"
                variant="h3"
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '40px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  color: '#FFFFFF'
                }}
              >
                Express Yourself
              </Typography>
              <Typography
                component="p"
                fontFamily="Inter"
                sx={{
                  mt: 2,
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '24px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  color: '#FFFFFFCC'
                }}
              >
                While upholding the value of human artistry in the age of AI
              </Typography>
              <StartCreatingButton />
            </Box>
          </PaddedContainer>
        )}
        <LoginTestComponent /> {/* For Login Form testing purposes */}
        <Footer />
      </>
    </div>
  );
};

export default OriginalLandingPage;

const LoginTestComponent = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await signIn({ username, password });
      await userClient.post('/login', {});
      navigate('/artist');
    } catch (error) {
      console.error(error, 'error');
    }
  };

  return (
    <form data-cy="login-form" onSubmit={handleSubmit} className="opacity-0 absolute -zIndex-1">
      <input data-cy="username-input" className="text-black" value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" />
      <input
        data-cy="password-input"
        className="text-black"
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder="Password"
      />
      <button data-cy="submit-button">Login</button>
    </form>
  );
};
